import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Shuffle as ShuffleIcon,
  CloudUpload as UploadIcon,
  PhotoCamera as CameraIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Cameraswitch as FlipCameraIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const CHARACTER_NAMES = [
  'Tiger', 'Spiderman', 'Luffy', 'Naruto', 'Batman', 'Goku',
  'Wolverine', 'Flash', 'Thor', 'Hulk', 'Deadpool', 'Ironman',
  'Zoro', 'Vegeta', 'Pikachu', 'Sonic', 'Mario', 'Link',
  'Joker', 'Venom', 'Thanos', 'Loki', 'Aquaman', 'Robin',
  'Sasuke', 'Itachi', 'Kakashi', 'Saitama', 'Genos', 'Tanjiro',
  'Eren', 'Levi', 'Mikasa', 'Megamind', 'Shrek', 'Groot',
  'Rocket', 'Drax', 'Gamora', 'Panther', 'Falcon', 'Hawkeye',
  'Phoenix', 'Storm', 'Raven', 'Beast', 'Cyborg', 'Arrow',
];

const PRESET_AVATARS = [
  { id: 'tiger', emoji: '🐯', label: 'Tiger' },
  { id: 'lion', emoji: '🦁', label: 'Lion' },
  { id: 'wolf', emoji: '🐺', label: 'Wolf' },
  { id: 'eagle', emoji: '🦅', label: 'Eagle' },
  { id: 'dragon', emoji: '🐉', label: 'Dragon' },
  { id: 'unicorn', emoji: '🦄', label: 'Unicorn' },
  { id: 'phoenix', emoji: '🔥', label: 'Phoenix' },
  { id: 'shark', emoji: '🦈', label: 'Shark' },
  { id: 'octopus', emoji: '🐙', label: 'Octopus' },
  { id: 'robot', emoji: '🤖', label: 'Robot' },
  { id: 'alien', emoji: '👽', label: 'Alien' },
  { id: 'ninja', emoji: '🥷', label: 'Ninja' },
  { id: 'wizard', emoji: '🧙', label: 'Wizard' },
  { id: 'skull', emoji: '💀', label: 'Skull' },
  { id: 'ghost', emoji: '👻', label: 'Ghost' },
  { id: 'panda', emoji: '🐼', label: 'Panda' },
  { id: 'fox', emoji: '🦊', label: 'Fox' },
  { id: 'cat', emoji: '🐱', label: 'Cat' },
];

const AVATAR_COLORS = [
  'linear-gradient(135deg, #ff00ff, #00ffff)',
  'linear-gradient(135deg, #ff6b6b, #ffa500)',
  'linear-gradient(135deg, #4ecdc4, #45b7d1)',
  'linear-gradient(135deg, #a855f7, #ec4899)',
  'linear-gradient(135deg, #06b6d4, #8b5cf6)',
  'linear-gradient(135deg, #f59e0b, #ef4444)',
  'linear-gradient(135deg, #10b981, #3b82f6)',
  'linear-gradient(135deg, #f97316, #a855f7)',
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255, 255, 255, 0.06)',
    color: '#fff',
    borderRadius: '12px',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 0, 255, 0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#ff00ff', borderWidth: '2px' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#ff00ff' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.5)' },
};

const GuestSetupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { guestLogin, resumeGuestSession, user } = useAuthContext();
  const defaultRedirect = location.state?.from || '/mindyourmusic';
  const fileInputRef = useRef(null);
  const selfieInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  // Returning guest — skip setup and go straight in
  useEffect(() => {
    let cancelled = false;

    const skipSetupIfExistingGuest = async () => {
      if (user?.user_type === 'guest') {
        navigate(defaultRedirect, { replace: true });
        return;
      }

      try {
        const response = await resumeGuestSession();
        if (!cancelled && response.success && response.exists) {
          navigate(defaultRedirect, { replace: true });
        }
      } catch (_) {}
    };

    skipSetupIfExistingGuest();
    return () => { cancelled = true; };
  }, [user, defaultRedirect, navigate, resumeGuestSession]);

  const [guestName, setGuestName] = useState('');
  const [nameError, setNameError] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [facingMode, setFacingMode] = useState('user');

  const suggestedNames = useMemo(() => {
    const shuffled = [...CHARACTER_NAMES].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 8);
  }, []);

  const [suggestions, setSuggestions] = useState(suggestedNames);

  const reshuffleSuggestions = () => {
    const shuffled = [...CHARACTER_NAMES].sort(() => Math.random() - 0.5);
    setSuggestions(shuffled.slice(0, 8));
  };

  const handleSelectAvatar = (avatar) => {
    setSelectedAvatar(avatar);
    setUploadedImage(null);
    setUploadedImageUrl(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async (mode = facingMode) => {
    stopCamera();
    setCameraError('');
    setCameraReady(false);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera not supported on this device. Use the file picker instead.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 720 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
      }
    } catch (err) {
      const message =
        err.name === 'NotAllowedError'
          ? 'Camera permission denied. Allow camera access or use Upload Photo.'
          : err.name === 'NotFoundError'
            ? 'No camera found. Use Upload Photo instead.'
            : 'Could not open camera. Try Upload Photo instead.';
      setCameraError(message);
    }
  }, [facingMode, stopCamera]);

  const handleOpenSelfie = async () => {
    // Prefer in-app camera when available; fall back to native capture input
    if (!navigator.mediaDevices?.getUserMedia) {
      selfieInputRef.current?.click();
      return;
    }
    setFacingMode('user');
    setCameraError('');
    setCameraReady(false);
    setSelfieOpen(true);
  };

  const handleCloseSelfie = () => {
    stopCamera();
    setSelfieOpen(false);
    setCameraError('');
  };

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleFlipCamera = () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    startCamera(next);
  };

  const uploadAvatarFile = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setAlertMessage('Image must be under 3MB');
      return;
    }
    if (!/^image\/(jpeg|png|gif|webp)$/.test(file.type)) {
      setAlertMessage('Only JPEG, PNG, GIF, or WebP images');
      return;
    }

    setIsUploading(true);
    setAlertMessage('');
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await fetch(`${API_BASE}/api/upload/avatar`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setUploadedImageUrl(data.imageUrl);
        setUploadedImage(URL.createObjectURL(file));
        setSelectedAvatar(null);
      } else {
        setAlertMessage(data.error || 'Upload failed');
      }
    } catch {
      setAlertMessage('Upload failed. Try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadAvatarFile(file);
    e.target.value = '';
  };

  const handleCaptureSelfie = async () => {
    const video = videoRef.current;
    if (!video || !cameraReady) return;

    const size = Math.min(video.videoWidth, video.videoHeight);
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;

    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Mirror selfie so it matches the preview
    if (facingMode === 'user') {
      ctx.translate(size, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setAlertMessage('Failed to capture selfie');
        return;
      }
      const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
      handleCloseSelfie();
      await uploadAvatarFile(file);
    }, 'image/jpeg', 0.92);
  };

  const clearUploadedImage = () => {
    setUploadedImage(null);
    setUploadedImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (selfieInputRef.current) selfieInputRef.current.value = '';
  };

  const getProfilePictureValue = () => {
    if (uploadedImageUrl) return uploadedImageUrl;
    if (selectedAvatar) return `emoji:${selectedAvatar.emoji}`;
    return null;
  };

  const handleContinue = async () => {
    const trimmed = guestName.trim();
    if (!trimmed) {
      setNameError('Pick a name to continue');
      return;
    }
    if (trimmed.length > 40) {
      setNameError('Name must be 40 characters or less');
      return;
    }
    setNameError('');
    setIsLoading(true);
    setAlertMessage('');
    try {
      const response = await guestLogin({
        guestName: trimmed,
        profilePicture: getProfilePictureValue(),
      });
      if (response.success) {
        navigate(defaultRedirect, { replace: true });
      }
    } catch (err) {
      setAlertMessage(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const avatarBg = selectedAvatar
    ? AVATAR_COLORS[PRESET_AVATARS.indexOf(selectedAvatar) % AVATAR_COLORS.length]
    : 'linear-gradient(135deg, rgba(255,0,255,0.3), rgba(0,255,255,0.3))';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: [
          'radial-gradient(ellipse 100% 60% at 50% 0%, rgba(255,0,255,0.2) 0%, transparent 50%)',
          'radial-gradient(circle at 15% 85%, rgba(0,255,255,0.12) 0%, transparent 40%)',
          'radial-gradient(circle at 85% 15%, rgba(255,0,255,0.12) 0%, transparent 40%)',
          'linear-gradient(180deg, #0a0015 0%, #150025 35%, #1a0033 70%, #0a0015 100%)',
        ].join(', '),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: { xs: 2, sm: 3 },
        px: { xs: 1.5, sm: 2 },
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 480 }}>
      {/* Back */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          color: 'rgba(255,255,255,0.7)',
          textTransform: 'none',
          fontWeight: 600,
          mb: 1,
          px: 0,
          '&:hover': { color: '#ff00ff', bgcolor: 'transparent' },
        }}
      >
        Back
      </Button>

      {/* Header */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 800,
          background: 'linear-gradient(90deg, #ff00ff, #00ffff)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          mb: 0.5,
          textAlign: 'center',
        }}
      >
        Set Up Guest Profile
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3, textAlign: 'center' }}>
        Pick a name and avatar. Your Guest ID is assigned automatically.
      </Typography>

      {/* Card */}
      <Box
        sx={{
          width: '100%',
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,0,255,0.25)',
          borderRadius: '20px',
          boxShadow: '0 0 40px rgba(255,0,255,0.15)',
          p: { xs: 2, sm: 3 },
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        {alertMessage && (
          <Alert severity="error" onClose={() => setAlertMessage('')} sx={{ mb: 2, borderRadius: '12px' }}>
            {alertMessage}
          </Alert>
        )}

        {/* Avatar Preview */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, pt: 0.5, pb: guestName ? 1.5 : 0 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Avatar
              src={uploadedImage || undefined}
              sx={{
                width: { xs: 80, sm: 96 },
                height: { xs: 80, sm: 96 },
                fontSize: { xs: '2.5rem', sm: '3rem' },
                background: uploadedImage ? 'transparent' : avatarBg,
                border: '3px solid rgba(255,0,255,0.5)',
                boxShadow: '0 0 30px rgba(255,0,255,0.3)',
              }}
            >
              {!uploadedImage && (selectedAvatar?.emoji || '🎵')}
            </Avatar>
            {guestName && (
              <Chip
                label={guestName}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  maxWidth: { xs: 120, sm: 140 },
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Name Section */}
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 700, mb: 1 }}>
          Choose Your Name
        </Typography>
        <TextField
          fullWidth
          label="Guest Name"
          value={guestName}
          onChange={(e) => {
            setGuestName(e.target.value);
            if (nameError) setNameError('');
          }}
          error={!!nameError}
          helperText={nameError || 'This is how you appear in games and leaderboards'}
          margin="none"
          sx={inputSx}
        />

        {/* Name Suggestions */}
        <Box sx={{ mt: 1.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Suggestions
            </Typography>
            <IconButton size="small" onClick={reshuffleSuggestions} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#00ffff' } }}>
              <ShuffleIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            {suggestions.map((name) => (
              <Chip
                key={name}
                label={name}
                size="small"
                onClick={() => { setGuestName(name); setNameError(''); }}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  bgcolor: guestName === name ? 'rgba(255,0,255,0.3)' : 'rgba(255,255,255,0.08)',
                  color: guestName === name ? '#ff00ff' : 'rgba(255,255,255,0.8)',
                  border: guestName === name ? '1px solid rgba(255,0,255,0.6)' : '1px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255,0,255,0.15)',
                    borderColor: 'rgba(255,0,255,0.4)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Avatar Section */}
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 700, mb: 1.5 }}>
          Choose Your Avatar
        </Typography>

        {/* Upload / Selfie Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <input
            ref={selfieInputRef}
            type="file"
            accept="image/*"
            capture="user"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            onClick={handleUploadClick}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            sx={{
              borderColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 600,
              '&:hover': { borderColor: '#00ffff', bgcolor: 'rgba(0,255,255,0.08)' },
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload Photo'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleOpenSelfie}
            disabled={isUploading}
            startIcon={<CameraIcon />}
            sx={{
              borderColor: 'rgba(255,0,255,0.35)',
              color: '#fff',
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 600,
              '&:hover': { borderColor: '#ff00ff', bgcolor: 'rgba(255,0,255,0.08)' },
            }}
          >
            Take a Selfie
          </Button>
          {uploadedImage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
              <Typography variant="caption" sx={{ color: '#4CAF50' }}>Uploaded</Typography>
              <IconButton size="small" onClick={clearUploadedImage} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Selfie Camera Dialog */}
        <Dialog
          open={selfieOpen}
          onClose={handleCloseSelfie}
          maxWidth="xs"
          fullWidth
          TransitionProps={{
            onEntered: () => startCamera('user'),
          }}
          PaperProps={{
            sx: {
              bgcolor: '#0a0015',
              border: '1px solid rgba(255,0,255,0.35)',
              borderRadius: '16px',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              color: '#fff',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              px: 2,
            }}
          >
            Take a Selfie
            <IconButton onClick={handleCloseSelfie} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2, pb: 1 }}>
            {cameraError ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>{cameraError}</Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleCloseSelfie();
                    selfieInputRef.current?.click();
                  }}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '10px',
                  }}
                >
                  Use Device Camera
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  bgcolor: '#000',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <Box
                  component="video"
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                  }}
                />
                {!cameraReady && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.6)',
                    }}
                  >
                    <CircularProgress size={36} sx={{ color: '#ff00ff' }} />
                  </Box>
                )}
                <IconButton
                  onClick={handleFlipCamera}
                  disabled={!cameraReady}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(255,0,255,0.4)' },
                  }}
                >
                  <FlipCameraIcon />
                </IconButton>
              </Box>
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2, pt: 1, justifyContent: 'center', gap: 1 }}>
            <Button
              onClick={handleCloseSelfie}
              sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
            >
              Cancel
            </Button>
            {!cameraError && (
              <Button
                variant="contained"
                onClick={handleCaptureSelfie}
                disabled={!cameraReady || isUploading}
                startIcon={<CameraIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '10px',
                  px: 3,
                  background: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',
                  '&:disabled': {
                    background: 'rgba(80,80,80,0.6)',
                    color: 'rgba(255,255,255,0.5)',
                  },
                }}
              >
                Capture
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Preset Avatars Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(5, minmax(0, 1fr))',
              sm: 'repeat(6, minmax(0, 1fr))',
            },
            gap: { xs: 0.5, sm: 0.75 },
            mb: 3,
            width: '100%',
          }}
        >
          {PRESET_AVATARS.map((avatar, idx) => {
            const isSelected = selectedAvatar?.id === avatar.id;
            const bg = AVATAR_COLORS[idx % AVATAR_COLORS.length];
            return (
              <Box
                key={avatar.id}
                onClick={() => handleSelectAvatar(avatar)}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  aspectRatio: '1',
                  cursor: 'pointer',
                  borderRadius: '10px',
                  p: 0.25,
                  boxSizing: 'border-box',
                  minWidth: 0,
                  border: isSelected ? '2px solid #ff00ff' : '2px solid transparent',
                  bgcolor: isSelected ? 'rgba(255,0,255,0.15)' : 'transparent',
                  transition: 'background-color 0.2s, border-color 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: { xs: 36, sm: 44 },
                    maxHeight: { xs: 36, sm: 44 },
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    background: bg,
                    boxShadow: isSelected ? '0 0 12px rgba(255,0,255,0.5)' : 'none',
                  }}
                >
                  {avatar.emoji}
                </Avatar>
              </Box>
            );
          })}
        </Box>

        {/* Continue Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleContinue}
          disabled={isLoading || !guestName.trim()}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            boxShadow: '0 4px 24px rgba(255,0,255,0.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
              boxShadow: '0 6px 28px rgba(255,0,255,0.45)',
            },
            '&:disabled': {
              background: 'rgba(80,80,80,0.6)',
              color: 'rgba(255,255,255,0.5)',
            },
          }}
        >
          {isLoading ? <CircularProgress size={26} color="inherit" /> : 'Continue as Guest'}
        </Button>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', textAlign: 'center', mt: 2 }}>
          Your Guest ID (e.g. MusicLover_42) is assigned automatically and shown in your profile.
        </Typography>
      </Box>
      </Box>
    </Box>
  );
};

export default GuestSetupPage;
